import type {
  JSONContent,
  MarkdownParseHelpers,
  MarkdownRendererHelpers,
  MarkdownToken,
  MarkdownTokenizer,
} from '@tiptap/core';
import { type Marked, Tokenizer, type TokenizerObject, type Tokens } from 'marked';

import { createAtxOnlyMarked } from '../atx-only-marked';
import { createHtmlLiteralizationTokenizer } from '../html-literalization';
import { normalizeLegacyBlankLines } from '../legacy-blank-lines';
import { headerlessTableTokenizer } from '../table-markdown';
import { DRAFT_SECTION_SENTINEL } from './draft-sentinel';
import {
  createEmptySectionHeadingMarker,
  isEmptySectionHeadingContent,
  isEmptySectionHeadingMarker,
  isSectionEndHtml,
  stripDraftSectionSentinel,
} from './section-markers';

type SectionMarkdownToken = MarkdownToken & {
  headingTokens?: MarkdownToken[];
  bodyTokens?: MarkdownToken[];
  emptyHeading?: boolean;
};

const SECTION_HEADING_LINE = /^ {0,3}#(?: ([^\r\n]*))?\r?(?:\n|$)/;
const SECTION_HEADING_IN_HTML_BLOCK = /\n {0,3}#(?: |(?=\r?(?:\n|$)))/;

// marked's html block (CommonMark type 6) runs until a blank line, so it would
// swallow a `# ` line glued to a preceding html block (`…</ul>\n# Plan`). The
// section convention wins: cut the html token just before the heading line and
// let marked re-lex the remainder, which then produces a real heading token.
// `pre` tokens (<pre>/<script>/<style>) end at their closing tag instead — a
// `# ` line inside them is verbatim content, never a section boundary.
function sectionBoundaryHtml(this: Tokenizer, src: string): Tokens.HTML | undefined {
  const token = Tokenizer.prototype.html.call(this, src);
  if (!token || token.pre) return token;
  const cut = SECTION_HEADING_IN_HTML_BLOCK.exec(token.raw);
  if (!cut) return token;
  const raw = token.raw.slice(0, cut.index + 1);
  return { ...token, raw, text: raw };
}

export const sectionBoundaryHtmlTokenizer: TokenizerObject = {
  html: sectionBoundaryHtml,
};

// The markdown-native marked: ATX-only headings, one html tokenizer that
// composes the section-boundary cut above with the literalization policy — tags
// the schema would delete come back as text — and the headerless-table
// convention. Fresh per call so the overrides (and the node tokens
// @tiptap/markdown registers at editor init) stay off the global singleton.
export const createMarkdownNativeMarked = (): Marked => {
  const instance = createAtxOnlyMarked()
    .use({ tokenizer: createHtmlLiteralizationTokenizer(sectionBoundaryHtml) })
    .use({ tokenizer: headerlessTableTokenizer });

  // TipTap parses via `markedInstance.lexer(src)`, which marked implements as
  // the original Lexer class — not `this.Lexer`. Wrap that method so vMDE
  // extra-newline runs become `&nbsp;` empty paragraphs on every MDNE load
  // (editor, paste, nested cell lex). The rewrite is a fixed point on MDNE's
  // own vocabulary.
  const lex = instance.lexer.bind(instance);
  instance.lexer = (src, options) => lex(normalizeLegacyBlankLines(src), options);

  return instance;
};

// Section delimiter rule: a line GFM lexes as an ATX H1 opens a section — `# `
// or the legacy empty `#` form at line start, allowing up to 3 leading spaces.
// 4+ spaces is indented code and `#` preceded by text is plain text, exactly as
// marked decides.
// The body boundary is not scanned by hand either: marked lexes the
// remainder and the body is every token before the next section (or
// top-level H1), so fences, lists, and html blocks follow marked's grammar.
// Re-entrancy guard: while a section body is being lexed, the next `# ` line
// must lex as a plain ATX heading (the loop's stop condition), not recurse
// into this tokenizer — each level would re-lex its whole remainder, making
// parse time exponential in the section count. Safe as module state: parsing
// is synchronous and sections never nest.
let lexingSectionBody = false;

export const sectionMarkdownTokenizer: MarkdownTokenizer = {
  name: 'sectionNode',
  level: 'block',
  start: (src: string) => src.match(/(?:^|\n) {0,3}#(?: |(?=\r?(?:\n|$)))/)?.index ?? -1,
  tokenize: (src, _tokens, lexer) => {
    if (lexingSectionBody) return undefined;
    const headingMatch = SECTION_HEADING_LINE.exec(src);
    if (!headingMatch) return undefined;

    const headingLine = headingMatch[0];
    const headingText = (headingMatch[1] ?? '').trim();
    const emptyHeading = headingText === '' || isEmptySectionHeadingMarker(headingText);

    const bodyTokens: MarkdownToken[] = [];
    let raw = headingLine;
    lexingSectionBody = true;
    try {
      for (const token of lexer.blockTokens(src.slice(headingLine.length))) {
        const opensNextSection = token.type === 'heading' && token.depth === 1;
        if (opensNextSection) break;
        // An explicit section-close marker ends the body so the
        // block after it stays top-level instead of being absorbed. Consume it
        // into `raw` (marked resumes after it) but never into `bodyTokens`.
        if (token.type === 'html' && isSectionEndHtml(token.raw)) {
          raw += token.raw ?? '';
          break;
        }
        bodyTokens.push(token);
        raw += token.raw ?? '';
      }
    } finally {
      lexingSectionBody = false;
    }

    return {
      type: 'sectionNode',
      raw,
      headingTokens: emptyHeading ? [] : lexer.inlineTokens(headingText),
      bodyTokens,
      emptyHeading,
      tokens: [],
    };
  },
};

const inlineText = (nodes: JSONContent[]): string =>
  nodes.map((node) => node.text ?? inlineText(node.content ?? [])).join('');

export const isEmptySectionNode = (node: JSONContent): boolean => {
  const heading = node.content?.[0];
  return (
    node.type === 'sectionNode' &&
    heading?.type === 'heading' &&
    isEmptySectionHeadingContent(inlineText(heading.content ?? []))
  );
};

const emptyHeadingMarkerIdentities = new WeakMap<JSONContent, number>();

/**
 * Supplies the document-order identity that a section renderer cannot derive
 * from its own subtree. The shallow clone keeps this serialization-only
 * metadata out of ProseMirror attrs and persisted editor JSON.
 *
 * The returned object IS the lookup key: the renderer dispatch between here and
 * `renderSectionMarkdown` must pass this exact reference through, unwrapped and
 * un-normalized. Losing it drops every section to the unnumbered fallback,
 * which is the duplicate-marker bug the identity exists to prevent. The clone
 * is shallow, so `content` is shared with the original — serialization only
 * ever reads it.
 */
export const withEmptyHeadingMarkerIdentity = (
  node: JSONContent,
  identity: number
): JSONContent => {
  const numberedNode = { ...node };
  emptyHeadingMarkerIdentities.set(numberedNode, identity);
  return numberedNode;
};

export const parseSectionMarkdown = (
  token: MarkdownToken,
  helpers: MarkdownParseHelpers
): JSONContent => {
  const {
    headingTokens = [],
    bodyTokens = [],
    emptyHeading,
  } = token as SectionMarkdownToken;
  const headingContent = emptyHeading
    ? [{ type: 'text', text: DRAFT_SECTION_SENTINEL }]
    : helpers.parseInline(headingTokens);
  const sectionTitle = emptyHeading ? 'New Section' : inlineText(headingContent);
  const heading = helpers.createNode('heading', { level: 1 }, headingContent);
  const body = helpers.parseChildren(bodyTokens);

  // `block+` is satisfied by the heading alone when the body is empty.
  return helpers.createNode('sectionNode', { sectionTitle }, [heading, ...body]);
};

// The draft sentinel only ever occupies an empty section heading, so removal is
// scoped to heading subtrees — mirroring the HTML presentation path, which
// strips U+200B under `<h1>` only. A legitimately pasted zero-width space in
// clinical body text must survive serialization untouched.
const stripDraftSentinelInHeadings = (
  node: JSONContent,
  inHeading = false
): JSONContent => {
  const insideHeading = inHeading || node.type === 'heading';
  return {
    ...node,
    ...(insideHeading && node.text !== undefined
      ? { text: stripDraftSectionSentinel(node.text) }
      : {}),
    ...(node.content === undefined
      ? {}
      : {
          content: node.content.map((child) =>
            stripDraftSentinelInHeadings(child, insideHeading)
          ),
        }),
  };
};

/**
 * Converts section-node JSON to presentation-only markdown content. Empty
 * section headings are structural persistence details, so flatten their body
 * into the surrounding document instead of post-processing serialized lines
 * (which could mistake a literal `#` inside a code block for a heading).
 */
export const toSectionPresentationContent = (content: JSONContent[]): JSONContent[] =>
  content.flatMap((node) => {
    const [, ...body] = node.content ?? [];

    return isEmptySectionNode(node)
      ? body.map((child) => stripDraftSentinelInHeadings(child))
      : [stripDraftSentinelInHeadings(node)];
  });

/** Serialize presentation content without doc-level persistence delimiters. */
export const serializeSectionPresentationMarkdown = (
  content: JSONContent[],
  serialize: (node: JSONContent) => string
): string =>
  toSectionPresentationContent(content)
    .map(serialize)
    .filter((part) => part.length > 0)
    .join('\n\n');

// The heading child normally renders itself as `# title`; an empty heading is
// rendered explicitly so trimming layers cannot collapse it to an ambiguous
// `#`/`# ` line. The doc node supplies separators between sibling sections.
export const renderSectionMarkdown = (
  node: JSONContent,
  helpers: MarkdownRendererHelpers
): string => {
  if (!node.content?.length) return '';
  const [heading, ...body] = node.content;
  if (!heading) return '';

  const headingText = inlineText(heading.content ?? []);
  if (isEmptySectionHeadingContent(headingText)) {
    const renderedBody = helpers.renderChildren(body, '\n\n');
    // No identity means this section is being rendered on its own rather than
    // through the document renderer, which stamps every empty section. `1` is
    // correct there: a lone section cannot collide with a sibling.
    const markerIdentity = emptyHeadingMarkerIdentities.get(node) ?? 1;
    return [`# ${createEmptySectionHeadingMarker(markerIdentity)}`, renderedBody]
      .filter((part) => part.length > 0)
      .join('\n\n');
  }

  return helpers.renderChildren([stripDraftSentinelInHeadings(heading), ...body], '\n\n');
};

export const sectionMarkdownSpec = {
  markdownTokenizer: sectionMarkdownTokenizer,
  parseMarkdown: parseSectionMarkdown,
  renderMarkdown: renderSectionMarkdown,
};
